// GLSL shared by every layer: how a place in the world (land, year) becomes a place on screen, and the
// noise behind the hand-drawn look. The same arithmetic lives in view/camera.ts for the CPU.

import { ROLL } from "../view/camera.ts";

export const MAX_COLUMNS = 32;

export const MAPPING = /* glsl */ `
uniform float uEdges[${MAX_COLUMNS + 1}];  // lane edges, px from the plot's top
uniform float uColumns;                   // number of lanes
uniform float uSplits[${MAX_COLUMNS}];     // where each lane's halves meet, 0..1
uniform vec4 uPlot;      // left, top, width, height (css px)
uniform vec2 uWindow;    // the time window, u0 and u1
uniform vec2 uViewport;  // css px
const float ROLL = ${ROLL.toFixed(4)};
const float ROLL_K = ${(ROLL / (1 - 2 * ROLL)).toFixed(6)};

// See rollOut in view/camera.ts: flat in the middle, wound up past the window's ends.
float rollOut(float t) {
  if (t < 0.0) return ROLL * exp(t / ROLL_K);
  if (t > 1.0) return 1.0 - ROLL * exp(-(t - 1.0) / ROLL_K);
  return ROLL + t * (1.0 - 2.0 * ROLL);
}
// A year is given by its u: 0 at the axis start, 1 at its end, every year the same length.
float timeX(float u) {
  return uPlot.x + uPlot.z * rollOut((u - uWindow.x) / (uWindow.y - uWindow.x));
}
float laneY(float c) {
  float i = clamp(floor(c), 0.0, uColumns - 1.0);
  int k = int(i);
  float f = c - i, s = uSplits[k];
  float g = f <= 0.5 ? (f / 0.5) * s : s + ((f - 0.5) / 0.5) * (1.0 - s);
  return uPlot.y + mix(uEdges[k], uEdges[k + 1], g);
}
// 0 on the flat of the scroll, 1 deep in a rolled end.
float rollDepth(float px) {
  float s = (px - uPlot.x) / uPlot.z;
  return clamp(max((ROLL - s) / ROLL, (s - (1.0 - ROLL)) / ROLL), 0.0, 1.0);
}
vec4 toClip(vec2 px) {
  return vec4(px.x / uViewport.x * 2.0 - 1.0, 1.0 - px.y / uViewport.y * 2.0, 0.0, 1.0);
}
`;

export const NOISE = /* glsl */ `
float hash11(float n) { return fract(sin(n * 127.1) * 43758.5453123); }
float hash21(float a, float b) { return fract(sin(a * 127.1 + b * 311.7) * 43758.5453123); }
float vnoise(float u, float seed) {
  float i = floor(u), f = fract(u);
  float w = f * f * (3.0 - 2.0 * f);
  return mix(hash21(i, seed), hash21(i + 1.0, seed), w) * 2.0 - 1.0;
}
// A pen line's wander, -1..1. Seeded by the line, so two washes that share an edge share its wobble.
float wobble(float u, float seed) {
  return vnoise(u, seed) * 0.72 + vnoise(u * 2.3 + 11.0, seed + 7.0) * 0.28;
}
// The same wobble at a steady size on screen at any zoom: octaves a factor of two apart, crossfaded.
float wobbleAtZoom(float worldU, float seed, float zoom) {
  float z = floor(zoom), t = zoom - z;
  float a = wobble(worldU * exp2(z), seed + z * 13.0);
  float b = wobble(worldU * exp2(z + 1.0), seed + (z + 1.0) * 13.0);
  return mix(a, b, t);
}
`;

export const EDITION = /* glsl */ `
uniform float uNight;
uniform vec3 uPigDay[10];
uniform vec3 uPigNight[10];
uniform vec3 uPaper[2];
uniform vec3 uGap[2];
uniform vec3 uPool[2];
uniform vec3 uInk[2];
vec3 pigment(float i) { int k = int(i); return mix(uPigDay[k], uPigNight[k], uNight); }
vec3 paperTone() { return mix(uPaper[0], uPaper[1], uNight); }
vec3 gapTone() { return mix(uGap[0], uGap[1], uNight); }
vec3 poolTone() { return mix(uPool[0], uPool[1], uNight); }
vec3 inkTone() { return mix(uInk[0], uInk[1], uNight); }
// A raking light that follows the pointer across the paper's grain. n is the surface's tilt.
uniform vec2 uLight;     // pointer, css px
uniform float uLightOn;  // 0..1
float raking(vec2 px, vec2 n) {
  vec2 toLight = uLight - px;
  float fall = exp(-dot(toLight, toLight) / (2.0 * 380.0 * 380.0));
  vec3 l = normalize(vec3(toLight, 260.0));
  return dot(normalize(vec3(n, 1.0)), l) * fall * uLightOn;
}
`;
